import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseApiError, getErrorMessage } from '../../utils/errorUtils';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../Messages';
import './CreateInvoiceModal.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';

// Days of credit per payment term, same rule as the backend (_calculate_due_date)
export const termDays = (terms) => {
  const code = (terms || '').toUpperCase().replace(/_/g, ' ').trim();
  if (['UPFRONT', 'DUE ON RECEIPT', 'COD', 'CASH'].includes(code)) return 0;
  const match = code.match(/^NET\s*(\d+)$/);
  return match ? parseInt(match[1], 10) : 30;
};

// invoiceDate is YYYY-MM-DD; computed in UTC so the time zone never shifts the day
export const dueDateFor = (invoiceDate, terms) => {
  const date = new Date(`${invoiceDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + termDays(terms));
  return date.toISOString().split('T')[0];
};

// Stored order terms ("NET_30") or legacy text ("Net 30") mapped to the select's codes
const normalizeTerms = (terms) => {
  const days = termDays(terms);
  return days === 0 ? 'UPFRONT' : `NET_${days}`;
};

function CreateInvoiceModal({ salesOrder, onClose, onInvoiceCreated }) {
  const { t } = useTranslation('sales');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(() => {
    const invoiceDate = localToday();
    const terms = normalizeTerms(salesOrder?.payment_terms || 'NET_30');
    return {
      invoice_date: invoiceDate,
      due_date: dueDateFor(invoiceDate, terms),
      payment_terms: terms,
      notes: ''
    };
  });

  // Changing the date or the terms recalculates the due date; it can still be edited by hand afterwards
  const handleFieldChange = (field, value) => {
    setInvoiceData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'invoice_date' || field === 'payment_terms') {
        next.due_date = dueDateFor(next.invoice_date, next.payment_terms) || prev.due_date;
      }
      return next;
    });
  };

  const handleCreateInvoice = async () => {
    try {
      setLoading(true);

      const requestData = {
        invoice_date: invoiceData.invoice_date,
        due_date: invoiceData.due_date,
        payment_terms: invoiceData.payment_terms,
        notes: invoiceData.notes
      };

      const response = await apiFetch(getApiUrl(`/api/invoices/generate-from-order/${salesOrder.sales_order_id}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(
          t('createInvoiceModal.toasts.createdTitle'),
          t('createInvoiceModal.toasts.createdMessage', { invoiceNumber: result.invoice_number, orderNumber: salesOrder.so_number })
        );

        if (onInvoiceCreated) {
          onInvoiceCreated(result);
        }

        onClose();
      } else {
        // Backend errors are English sentences; show them translated (#383)
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData
          ? getErrorMessage(parseApiError(errorData), tCommon)
          : t('createInvoiceModal.toasts.createFailedMessage'));
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      showError(t('invoices.view.messages.failedToCreateInvoice'), error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common:ui.notAvailable');
    return formatAppDate(dateString);
  };

  return (
    <div className="create-invoice-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('createInvoiceModal.title')}</h2>
          <button className="close-button" onClick={onClose} disabled={loading}>×</button>
        </div>

        <div className="modal-body">
          {/* Sales Order Summary */}
          <div className="order-summary">
            <h3>{t('createInvoiceModal.salesOrderDetails')}</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <label>{t('salesOrders.view.fields.orderNumber')}:</label>
                <span>{salesOrder?.so_number}</span>
              </div>
              <div className="summary-item">
                <label>{t('common:ui.customer')}:</label>
                <span>{salesOrder?.customer_name}</span>
              </div>
              <div className="summary-item">
                <label>{t('salesOrders.view.fields.orderDate')}:</label>
                <span>{formatDate(salesOrder?.order_date)}</span>
              </div>
              <div className="summary-item">
                <label>{t('salesOrders.view.fields.totalAmount')}:</label>
                <span className="amount">{formatCurrency(salesOrder?.total_amount)}</span>
              </div>
              <div className="summary-item">
                <label>{t('common:ui.status')}:</label>
                <span className={`status-badge ${salesOrder?.status?.toLowerCase()}`}>
                  {salesOrder?.status}
                </span>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="invoice-settings">
            <h3>{t('createInvoiceModal.invoiceSettings')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('invoices.view.fields.invoiceDate')} *</label>
                <input
                  type="date"
                  value={invoiceData.invoice_date}
                  onChange={(e) => handleFieldChange('invoice_date', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('invoices.view.fields.dueDate')} *</label>
                <input
                  type="date"
                  value={invoiceData.due_date}
                  onChange={(e) => handleFieldChange('due_date', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('invoices.view.fields.paymentTerms')}</label>
                <select
                  value={invoiceData.payment_terms}
                  onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
                >
                  <option value="UPFRONT">{t('quotations.view.paymentTermsOptions.upfront')}</option>
                  <option value="NET_30">{t('quotations.view.paymentTermsOptions.net30')}</option>
                  <option value="NET_45">{t('quotations.view.paymentTermsOptions.net45')}</option>
                  <option value="NET_60">{t('quotations.view.paymentTermsOptions.net60')}</option>
                  <option value="NET_90">{t('quotations.view.paymentTermsOptions.net90')}</option>
                  <option value="NET_120">{t('quotations.view.paymentTermsOptions.net120')}</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>{t('common:ui.notes')}</label>
                <textarea
                  value={invoiceData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  rows="3"
                  placeholder={t('createInvoiceModal.placeholders.notes')}
                />
              </div>
            </div>
          </div>

          {/* Line Items Information */}
          <div className="line-selection">
            <h3>{t('createInvoiceModal.lineItems')}</h3>
            <div className="line-items-note">
              <p><strong>{t('createInvoiceModal.note')}:</strong> {t('createInvoiceModal.allLinesIncluded')}</p>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common:ui.cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleCreateInvoice}
            disabled={loading || !invoiceData.invoice_date || !invoiceData.due_date ||
              invoiceData.due_date < invoiceData.invoice_date}
          >
            {loading ? t('createInvoiceModal.creating') : t('salesOrders.view.buttons.createInvoice')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateInvoiceModal;